import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, List, ListItem, ListItemText, Divider, Alert } from '@mui/material';
import { getAuthDebugInfo, refreshSessionIfNeeded } from '../../utils/authUtils';
import { buildApiUrl } from '../../utils/apiUtils';
import { supabase } from '../../supabaseClient';

/**
 * Auth Debugger Component
 * Displays authentication state and provides debugging tools
 */
const AuthDebugger = () => {
  const [authInfo, setAuthInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiTestResult, setApiTestResult] = useState(null);
  const [backendHealth, setBackendHealth] = useState(null);

  const loadAuthInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const info = await getAuthDebugInfo();
      setAuthInfo(info);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const testBackendHealth = async () => {
    try {
      const response = await fetch('http://localhost:3001/health');
      const data = await response.json();
      setBackendHealth(data);
    } catch (err) {
      setBackendHealth({ error: err.message });
    }
  };

  const testApiAuth = async () => {
    try {
      setApiTestResult({ status: 'testing' });
      const token = await supabase.auth.getSession().then(res => res.data.session?.access_token);
      
      if (!token) {
        setApiTestResult({ error: 'No authentication token available' });
        return;
      }
      
      // Test the debug auth endpoint
      const response = await fetch(buildApiUrl('debug/auth'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      setApiTestResult({
        status: response.status,
        statusText: response.statusText,
        data
      });
    } catch (err) {
      setApiTestResult({ error: err.message });
    }
  };

  const refreshToken = async () => {
    try {
      const result = await refreshSessionIfNeeded();
      if (result) {
        await loadAuthInfo();
        setError(null);
      } else {
        setError('Token refresh failed');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    loadAuthInfo();
    testBackendHealth();
  }, []);

  if (loading) {
    return <Typography>Loading authentication information...</Typography>;
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', my: 4 }}>
      <Typography variant="h5" gutterBottom>Authentication Debugger</Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Authentication Status</Typography>
        <List dense>
          <ListItem>
            <ListItemText 
              primary="Login Status" 
              secondary={authInfo?.isLoggedIn ? 'Logged In ✓' : 'Not Logged In ✗'} 
            />
          </ListItem>
          
          {authInfo?.isLoggedIn && (
            <>
              <ListItem>
                <ListItemText primary="User ID" secondary={authInfo.userId} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Email" secondary={authInfo.email} />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Token Expiry" 
                  secondary={authInfo.tokenExpiry || 'Unknown'} 
                />
              </ListItem>
            </>
          )}
          
          <ListItem>
            <ListItemText 
              primary="Supabase Environment" 
              secondary={authInfo?.isUsingLocalSupabase ? 'Local ✓' : 'Production'} 
            />
          </ListItem>
          <ListItem>
            <ListItemText primary="Supabase URL" secondary={authInfo?.supabaseUrl} />
          </ListItem>
        </List>
        
        <Button 
          variant="outlined" 
          onClick={refreshToken}
          sx={{ mr: 1, mt: 1 }}
        >
          Refresh Token
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={loadAuthInfo}
          sx={{ mt: 1 }}
        >
          Reload Info
        </Button>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Backend Health</Typography>
        <Button 
          variant="outlined" 
          onClick={testBackendHealth}
          sx={{ mb: 2, mt: 1 }}
        >
          Test Backend Health
        </Button>
        
        {backendHealth && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(backendHealth, null, 2)}
            </Typography>
          </Paper>
        )}
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Box>
        <Typography variant="h6">API Authentication Test</Typography>
        <Button 
          variant="outlined" 
          onClick={testApiAuth}
          sx={{ mb: 2, mt: 1 }}
        >
          Test API Auth
        </Button>
        
        {apiTestResult && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(apiTestResult, null, 2)}
            </Typography>
          </Paper>
        )}
      </Box>
    </Paper>
  );
};

export default AuthDebugger;